import React, { useEffect, useRef, useState } from 'react';

/* ─── Mobile hook ─── */
function useIsMobile(bp = 768) {
  const [mobile, setMobile] = useState(() => typeof window !== 'undefined' && window.innerWidth < bp);
  useEffect(() => {
    const fn = () => setMobile(window.innerWidth < bp);
    window.addEventListener('resize', fn);
    fn();
    return () => window.removeEventListener('resize', fn);
  }, [bp]);
  return mobile;
}

/* ─── Scroll reveal ─── */
function useReveal(threshold = 0.12) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) { setVisible(true); obs.disconnect(); }
    }, { threshold });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, visible];
}

/* ─── Animated counter ─── */
function Counter({ end, suffix = '' }) {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const started = useRef(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => {
      if (e.isIntersecting && !started.current) {
        started.current = true;
        let s = 0;
        const step = (ts) => {
          if (!s) s = ts;
          const prog = Math.min((ts - s) / 1800, 1);
          setCount(Math.floor((1 - Math.pow(1 - prog, 3)) * end));
          if (prog < 1) requestAnimationFrame(step); else setCount(end);
        };
        requestAnimationFrame(step);
      }
    }, { threshold: 0.3 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [end]);
  return <span ref={ref}>{count}{suffix}</span>;
}

/* ─── Reveal wrapper — always "up" on mobile ─── */
function Reveal({ children, delay = 0, direction = 'up' }) {
  const [ref, visible] = useReveal();
  const isMobile = useIsMobile();
  const dir = isMobile ? 'up' : direction;
  const t = { up: 'translateY(40px)', left: 'translateX(-50px)', right: 'translateX(50px)', fade: 'scale(0.93)' };
  return (
    <div ref={ref} style={{
      opacity: visible ? 1 : 0,
      transform: visible ? 'none' : t[dir],
      transition: `opacity 0.65s ease ${delay}s, transform 0.65s cubic-bezier(0.16,1,0.3,1) ${delay}s`,
      height: '100%',
    }}>
      {children}
    </div>
  );
}

/* ───────── DATA ───────── */
const services = [
  { icon: '⚡', title: 'Electronic Design',      desc: 'End-to-end embedded hardware & software design for IoT and smart devices.',             color: '#6366f1', image: 'https://images.pexels.com/photos/2582937/pexels-photo-2582937.jpeg?auto=compress&cs=tinysrgb&w=600' },
  { icon: '🔧', title: 'PCB Fabrication',        desc: 'Multi-layer PCB from 2 to 24 layers with fast turnaround and DFM validation.',          color: '#0ea5e9', image: 'https://images.pexels.com/photos/4779887/pexels-photo-4779887.jpeg?auto=compress&cs=tinysrgb&w=600' },
  { icon: '🏭', title: 'EMS Solutions',          desc: 'Turnkey manufacturing from 100 prototype units to 1M+ mass production.',                 color: '#10b981', image: 'https://images.pexels.com/photos/36522035/pexels-photo-36522035.jpeg' },
  { icon: '📦', title: 'Box Build Assembly',     desc: 'Complete product integration: PCB-to-enclosure, harness, and final testing.',           color: '#f59e0b', image: 'https://images.pexels.com/photos/1267338/pexels-photo-1267338.jpeg?auto=compress&cs=tinysrgb&w=600' },
  { icon: '🔗', title: 'Component Sourcing',     desc: 'Integrated supply chain ensuring quality components at competitive prices.',             color: '#ef4444', image: 'https://images.pexels.com/photos/163100/circuit-circuit-board-resistor-computer-163100.jpeg?auto=compress&cs=tinysrgb&w=600' },
  { icon: '✅', title: 'Testing & Validation',   desc: 'In-house validation, ICT, functional testing and traceability reporting.',               color: '#8b5cf6', image: 'https://images.pexels.com/photos/3912981/pexels-photo-3912981.jpeg?auto=compress&cs=tinysrgb&w=600' },
  { icon: '🔩', title: 'Mechanical Components', desc: 'Custom plastic and sheet metal enclosures with IP-rated protection.',                    color: '#06b6d4', image: 'https://images.pexels.com/photos/1108101/pexels-photo-1108101.jpeg?auto=compress&cs=tinysrgb&w=600' },
  { icon: '🌐', title: 'Wire Harness',           desc: 'Custom wire harnessing and mechanical assembly for complex builds.',                     color: '#ec4899', image: 'https://images.pexels.com/photos/2881232/pexels-photo-2881232.jpeg?auto=compress&cs=tinysrgb&w=600' },
];

const industries = [
  { icon: '🚗', name: 'Automotive',          desc: 'Robust electronics for vehicle systems requiring durability under extreme conditions.', color: '#6366f1', image: 'https://images.pexels.com/photos/3729464/pexels-photo-3729464.jpeg?auto=compress&cs=tinysrgb&w=600' },
  { icon: '🏥', name: 'Medical Devices',     desc: 'Precision manufacturing meeting stringent regulatory compliance standards.',             color: '#10b981', image: 'https://images.pexels.com/photos/4386466/pexels-photo-4386466.jpeg?auto=compress&cs=tinysrgb&w=600' },
  { icon: '⚙️', name: 'Industrial & IoT',   desc: 'Connected sensors, automation equipment and industrial control systems.',               color: '#f59e0b', image: 'https://images.pexels.com/photos/6048394/pexels-photo-6048394.jpeg' },
  { icon: '⚡', name: 'Energy Systems',      desc: 'Power management, renewable energy controllers and EV battery electronics.',            color: '#ef4444', image: 'https://images.pexels.com/photos/6876535/pexels-photo-6876535.jpeg' },
  { icon: '📱', name: 'Consumer Electronics',desc: 'Smart devices, wearables and connected home products with premium reliability.',        color: '#8b5cf6', image: 'https://images.pexels.com/photos/16888144/pexels-photo-16888144.jpeg' },
  { icon: '🌐', name: 'Global Footprint',    desc: 'Worldwide logistics capabilities ensuring delivery wherever you operate.',           color: '#0ea5e9', image: 'https://images.pexels.com/photos/4481326/pexels-photo-4481326.jpeg?auto=compress&cs=tinysrgb&w=600' },
];

const caseStudies = [
  { tag: 'AI / Consumer',    title: 'Smart AI Cooking Assistant Localization', stat: '3× Faster',        desc: 'Localized a smart AI cooking assistant for the Indian market, reducing BOM cost by 28% while maintaining CE/FCC compliance.',      image: 'https://images.pexels.com/photos/9028916/pexels-photo-9028916.jpeg', accent: '#6366f1' },
  { tag: 'EV / Energy',      title: 'EV Battery Production Scaling',           stat: '1M+ Units',        desc: 'Scaled EV battery management board production from 5K prototype to 1M+ units annually within 9 months.',                         image: 'https://images.pexels.com/photos/110844/pexels-photo-110844.jpeg?auto=compress&cs=tinysrgb&w=600',    accent: '#10b981' },
  { tag: 'Solar / Clean-tech',title: 'Solar Park Manufacturing Support',       stat: '40% Cost Reduction',desc: 'Designed and manufactured MPPT controllers for a 50MW solar farm, cutting BOM cost by 40%.',                                   image: 'https://images.pexels.com/photos/9875441/pexels-photo-9875441.jpeg?auto=compress&cs=tinysrgb&w=600',  accent: '#f59e0b' },
  { tag: 'Industrial',        title: 'Clean-tech Production Scaling',          stat: '98.7% Yield',      desc: "Achieved 98.7% first-pass yield for a cleantech client's industrial IoT sensor boards through SPC and AOI.",                   image: 'https://images.pexels.com/photos/16647824/pexels-photo-16647824.jpeg',  accent: '#ef4444' },
];

const whyCards = [
  { icon: '⚡', title: 'Speed to Market',        desc: 'Seamless collaboration from initial design through final delivery reduces development cycles.',          color: '#6366f1', bg: '#ede9fe' },
  { icon: '🛡️', title: 'Uncompromising Quality', desc: 'Industry certifications, best-in-class processes, and rigorous testing protocols.',                      color: '#10b981', bg: '#d1fae5' },
  { icon: '📈', title: 'Scalable Innovation',    desc: 'Defect-free quality and relentless innovation that keeps our clients ahead in the market.',               color: '#f59e0b', bg: '#fef3c7' },
  { icon: '🚀', title: 'Competitive Advantage',  desc: 'Cost-effective solutions without compromising innovation, reliability, or technical excellence.',          color: '#ef4444', bg: '#fee2e2' },
  { icon: '🤝', title: 'Dedicated Partnership',  desc: 'Our team acts as your extended operations partner throughout the product lifecycle.',                      color: '#8b5cf6', bg: '#ede9fe' },
  { icon: '🎯', title: 'Turnkey Solutions',       desc: 'Comprehensive electronic product design paired with manufacturing excellence.',                           color: '#0ea5e9', bg: '#e0f2fe' },
];

const statsData = [
  { value: 40,  suffix: 'K',     label: 'Sq ft Manufacturing Floor', color: '#6366f1' },
  { value: 1,   suffix: 'M+/hr', label: 'Components Placed',         color: '#10b981' },
  { value: 2,   suffix: '',      label: 'SMT Lines',                  color: '#f59e0b' },
  { value: 5,   suffix: '',      label: 'Through Hole Lines',         color: '#ef4444' },
  { value: 100, suffix: 'K',     label: 'Clean Room Class',           color: '#8b5cf6' },
  { value: 100, suffix: '%',     label: 'Full Traceability',          color: '#0ea5e9' },
];

const processSteps = [
  { step: '01', title: 'Requirement Discovery', desc: 'We understand your product vision, volume requirements, and compliance needs.',               color: '#6366f1', bg: '#ede9fe', img: 'https://media.istockphoto.com/id/918512860/photo/city.jpg?s=1024x1024&w=is&k=20&c=2RTz8c7DSf7JapOpMeKjoCpF4_jIPmVaNCGRyAbnw5k=' },
  { step: '02', title: 'Design & Prototyping',  desc: 'Our engineers create schematics, PCB layouts, and a working prototype for validation.',       color: '#10b981', bg: '#d1fae5', img: 'https://images.pexels.com/photos/19149826/pexels-photo-19149826.jpeg' },
  { step: '03', title: 'Component Sourcing',    desc: 'We source all components through our vetted supply chain at best-in-class pricing.',           color: '#f59e0b', bg: '#fef3c7', img: 'https://images.pexels.com/photos/7286028/pexels-photo-7286028.jpeg' },
  { step: '04', title: 'Manufacturing & Testing',desc: 'Automated SMT, THT assembly, ICT and functional testing in our certified facility.',          color: '#ef4444', bg: '#fee2e2', img: 'https://images.pexels.com/photos/132700/pexels-photo-132700.jpeg' },
  { step: '05', title: 'Box Build & Delivery',  desc: 'Final assembly, packing, and logistics — product-ready, on time, every time.',                color: '#8b5cf6', bg: '#ede9fe', img: 'https://images.pexels.com/photos/21838827/pexels-photo-21838827.jpeg' },
];

/* ═══════════════════════════════════════
   MAIN COMPONENT
═══════════════════════════════════════ */
export default function Home() {
  const isMobile = useIsMobile();
  const isTablet = useIsMobile(1024);

  /* ── responsive helpers ── */
  const heroGrid   = isMobile ? '1fr' : '1fr 1fr';
  const heroAlign  = isMobile ? 'center' : 'flex-start';
  const heroPad    = isMobile ? '80px 0 40px' : '120px 0 80px';
  const svcCols    = isMobile ? '1fr 1fr' : isTablet ? '1fr 1fr' : 'repeat(4,1fr)';
  const indCols    = isMobile ? '1fr' : isTablet ? '1fr 1fr' : 'repeat(3,1fr)';
  const whyCols    = isMobile ? '1fr' : isTablet ? '1fr 1fr' : 'repeat(3,1fr)';
  const statsCols  = isMobile ? '1fr 1fr' : isTablet ? 'repeat(3,1fr)' : 'repeat(6,1fr)';
  const ctaTrust   = isMobile ? '1fr 1fr' : 'repeat(4,1fr)';
  const heroStats  = isMobile ? '1fr 1fr' : 'repeat(4,1fr)';
  const sectionPad = isMobile ? '60px 0' : '100px 0';

  return (
    <main style={{ fontFamily: "'DM Sans','Inter',sans-serif", overflowX: 'hidden' }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;700;800&family=Space+Grotesk:wght@700;800&display=swap');
        *{box-sizing:border-box;margin:0;padding:0}
        a{text-decoration:none}
        img{display:block;max-width:100%}
        .btn-p{display:inline-flex;align-items:center;gap:8px;background:linear-gradient(135deg,#6366f1,#8b5cf6);color:#fff;font-weight:700;font-size:.95rem;padding:14px 28px;border-radius:50px;border:none;cursor:pointer;transition:all .3s;box-shadow:0 8px 30px rgba(99,102,241,.4);white-space:nowrap}
        .btn-p:hover{transform:translateY(-2px);box-shadow:0 14px 40px rgba(99,102,241,.55)}
        .btn-s{display:inline-flex;align-items:center;gap:8px;background:rgba(255,255,255,.1);color:#fff;font-weight:600;font-size:.9rem;padding:13px 26px;border-radius:50px;border:1px solid rgba(255,255,255,.25);cursor:pointer;transition:all .3s;backdrop-filter:blur(10px);white-space:nowrap}
        .btn-s:hover{background:rgba(255,255,255,.18);border-color:rgba(255,255,255,.45)}
        .container{max-width:1200px;margin:0 auto;padding:0 20px}
        .ch:hover{transform:translateY(-6px);transition:transform .35s cubic-bezier(.16,1,.3,1),box-shadow .35s ease}
        .iz img{transition:transform .6s ease}
        .iz:hover img{transform:scale(1.08)}
        @keyframes float{0%,100%{transform:translateY(0)}50%{transform:translateY(-14px)}}
        @keyframes marquee{0%{transform:translateX(0)}100%{transform:translateX(-50%)}}
        .fa{animation:float 4s ease-in-out infinite}
        .fa2{animation:float 5s 1s ease-in-out infinite}
        .section-tag{display:inline-block;font-size:.72rem;font-weight:700;letter-spacing:.1em;text-transform:uppercase;padding:5px 14px;border-radius:50px;margin-bottom:12px}
        .section-title{font-family:'Space Grotesk',sans-serif;font-size:clamp(1.7rem,4vw,3rem);font-weight:800;line-height:1.1;margin-bottom:14px}
      `}</style>

      {/* ══ HERO ══ */}
      <section style={{ background: 'linear-gradient(135deg,#0f0c29,#1a1045 40%,#0a2463)', position: 'relative', overflow: 'hidden' }}>
        {/* blobs */}
        {[[700,700,-200,-100,'rgba(99,102,241,.2)'],[500,500,100,null,'rgba(139,92,246,.15)'],[400,400,null,null,'rgba(14,165,233,.15)']].map(([w,h,top,left,c],i)=>(
          <div key={i} style={{position:'absolute',width:w,height:h,top:top??'auto',left:left??'auto',bottom:i===2?-100:'auto',right:i===1?-50:'auto',borderRadius:'50%',background:`radial-gradient(circle,${c} 0%,transparent 70%)`,filter:'blur(60px)',pointerEvents:'none'}}/>
        ))}
        <div style={{position:'absolute',inset:0,backgroundImage:'linear-gradient(rgba(255,255,255,.03) 1px,transparent 1px),linear-gradient(90deg,rgba(255,255,255,.03) 1px,transparent 1px)',backgroundSize:'60px 60px',pointerEvents:'none'}}/>

        <div className="container" style={{ display: 'grid', gridTemplateColumns: heroGrid, gap: isMobile ? 32 : 64, alignItems: 'center', padding: heroPad, paddingLeft: 20, paddingRight: 20 }}>
          {/* Left content */}
          <div style={{ textAlign: isMobile ? 'center' : 'left' }}>
            <div className="section-tag" style={{ background: 'linear-gradient(135deg,rgba(99,102,241,.25),rgba(139,92,246,.15))', color: '#a5b4fc', border: '1px solid rgba(99,102,241,.3)' }}>🏭 Electronics Manufacturing Platform</div>
            <h1 style={{ fontFamily: "'Space Grotesk',sans-serif", fontSize: isMobile ? 'clamp(2.2rem,8vw,3rem)' : 'clamp(2.8rem,5.5vw,4.5rem)', fontWeight: 800, color: '#fff', lineHeight: 1.05, marginBottom: 20 }}>
              Partner from<br />
              <span style={{ background: 'linear-gradient(90deg,#a5b4fc,#67e8f9,#6ee7b7)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>100 to 1 Million</span><br />
              Units
            </h1>
            <p style={{ fontSize: isMobile ? '1rem' : '1.1rem', color: 'rgba(255,255,255,.6)', lineHeight: 1.75, marginBottom: 28, maxWidth: isMobile ? '100%' : 460 }}>
              Top quality at competitive prices with turnkey manufacturing — from concept design to production-ready hardware.
            </p>
            <div style={{ display: 'flex', gap: 12, marginBottom: 24, flexWrap: 'wrap', justifyContent: isMobile ? 'center' : 'flex-start' }}>
              <a href="/contact" className="btn-p">Get a Free Quote →</a>
              <a href="/services" className="btn-s">Explore Services</a>
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, justifyContent: isMobile ? 'center' : 'flex-start' }}>
              {['✓ ISO 9001 Certified','✓ RoHS Compliant','✓ Class 100K Clean Room'].map(t=>(
                <span key={t} style={{ fontSize: '.78rem', fontWeight: 600, color: 'rgba(255,255,255,.45)' }}>{t}</span>
              ))}
            </div>
          </div>

          {/* Right image — hidden on very small, shown below content on mobile */}
          <div style={{ position: 'relative', order: isMobile ? -1 : 0 }}>
            {!isMobile && (
              <>
                <div className="fa" style={{ position:'absolute',top:'8%',right:'-5%',zIndex:10,background:'rgba(255,255,255,.12)',backdropFilter:'blur(20px)',border:'1px solid rgba(255,255,255,.2)',borderRadius:16,padding:'12px 18px',display:'flex',alignItems:'center',gap:10 }}>
                  <span style={{fontSize:'1.4rem'}}>📊</span>
                  <div><div style={{fontWeight:700,fontSize:'.82rem',color:'#fff'}}>98.7% Yield</div><div style={{fontSize:'.7rem',color:'rgba(255,255,255,.5)'}}>First-pass rate</div></div>
                </div>
                <div className="fa2" style={{ position:'absolute',bottom:'12%',left:'-6%',zIndex:10,background:'rgba(99,102,241,.2)',backdropFilter:'blur(20px)',border:'1px solid rgba(99,102,241,.4)',borderRadius:16,padding:'12px 18px',display:'flex',alignItems:'center',gap:10 }}>
                  <span style={{fontSize:'1.4rem'}}>🏭</span>
                  <div><div style={{fontWeight:700,fontSize:'.82rem',color:'#fff'}}>ISO 9001</div><div style={{fontSize:'.7rem',color:'rgba(255,255,255,.5)'}}>Certified facility</div></div>
                </div>
              </>
            )}
            <div className="iz" style={{ borderRadius: isMobile ? 16 : 24, overflow:'hidden', boxShadow:'0 40px 100px rgba(0,0,0,.5),0 0 0 1px rgba(255,255,255,.08)', transform: isMobile ? 'none' : 'perspective(1000px) rotateY(-4deg) rotateX(2deg)', transition:'transform .5s ease' }}>
              <img src="https://images.pexels.com/photos/2582937/pexels-photo-2582937.jpeg?auto=compress&cs=tinysrgb&w=800" alt="Electronics manufacturing" style={{ width:'100%', height: isMobile ? 220 : 440, objectFit:'cover' }} />
              <div style={{position:'absolute',inset:0,background:'linear-gradient(135deg,rgba(10,14,39,.45),rgba(99,102,241,.2))',pointerEvents:'none'}}/>
            </div>
          </div>
        </div>

        {/* Stats bar */}
        <div style={{ borderTop:'1px solid rgba(255,255,255,.08)', display:'grid', gridTemplateColumns: heroStats }}>
          {[{n:'40K sq ft',l:'Manufacturing Floor',c:'#a5b4fc'},{n:'1.5M/hr',l:'Components Placed',c:'#6ee7b7'},{n:'100–1M+',l:'Unit Scalability',c:'#fcd34d'},{n:'100%',l:'Traceability',c:'#f9a8d4'}].map((s,i)=>(
            <div key={i} style={{ padding: isMobile ? '18px 16px' : '28px 24px', borderRight: (i===1 && isMobile) || (i===3) ? 'none' : '1px solid rgba(255,255,255,.08)', borderTop: isMobile && i>=2 ? '1px solid rgba(255,255,255,.08)' : 'none', display:'flex',flexDirection:'column',gap:4 }}>
              <strong style={{fontFamily:"'Space Grotesk',sans-serif",fontSize: isMobile ? '1.3rem' : '1.6rem',fontWeight:800,color:s.c}}>{s.n}</strong>
              <span style={{fontSize:'.72rem',color:'rgba(255,255,255,.4)'}}>{s.l}</span>
            </div>
          ))}
        </div>
      </section>

      {/* ══ MARQUEE ══ */}
      <div style={{background:'#0f0c29',borderTop:'1px solid rgba(255,255,255,.05)',padding:'18px 0',overflow:'hidden'}}>
        <div style={{display:'flex',animation:'marquee 20s linear infinite',width:'max-content'}}>
          {[...Array(2)].map((_,r)=>(
            <div key={r} style={{display:'flex',gap:50,paddingRight:50}}>
              {['ISO 9001 Certified','RoHS Compliant','CE / FCC Ready','IATF 16949','IPC Class 3','UL Listed','AS9100 Aerospace'].map(l=>(
                <span key={l} style={{fontWeight:700,fontSize:'.75rem',letterSpacing:'.12em',color:'rgba(255,255,255,.2)',whiteSpace:'nowrap',textTransform:'uppercase'}}>{l}</span>
              ))}
            </div>
          ))}
        </div>
      </div>

      {/* ══ WHY CHOOSE ══ */}
     <section
  style={{
    background: 'linear-gradient(135deg, #ffe4ec, #fff0f5)',
    padding: sectionPad
  }}
>
  <div className="container">
    <Reveal>
      <div style={{ textAlign: 'center', marginBottom: isMobile ? 36 : 60 }}>
        <div
          className="section-tag"
          style={{ background: '#ede9fe', color: '#6d28d9' }}
        >
          About Zxeon FS
        </div>

        <h2
          className="section-title"
          style={{ color: '#0f0c29' }}
        >
          One Partner. <span style={{ color: '#6366f1' }}>Total Confidence.</span>
        </h2>

        <p
          style={{
            color: '#6b7280',
            lineHeight: 1.75,
            maxWidth: 540,
            margin: '0 auto',
            fontSize: '1rem'
          }}
        >
          Zxeon FS represents the convergence of cutting-edge electronic product design with comprehensive turnkey manufacturing solutions.
        </p>
      </div>
    </Reveal>

    <div
      style={{
        display: 'grid',
        gridTemplateColumns: whyCols,
        gap: 16
      }}
    >
      {whyCards.map((c, i) => (
        <Reveal
          key={c.title}
          delay={i * 0.07}
          direction={isMobile ? 'up' : i % 2 === 0 ? 'left' : 'right'}
        >
          <div
            className="ch"
            style={{
              background: c.bg,
              borderRadius: 20,
              padding: isMobile ? '24px 20px' : 32,
              border: `1px solid ${c.color}22`,
              position: 'relative',
              overflow: 'hidden',
              cursor: 'default',
              height: '100%'
            }}
          >
            <div
              style={{
                position: 'absolute',
                top: -20,
                right: -20,
                width: 70,
                height: 70,
                borderRadius: '50%',
                background: c.color,
                opacity: 0.08
              }}
            />

            <div style={{ fontSize: '2rem', marginBottom: 12 }}>
              {c.icon}
            </div>

            <h3
              style={{
                fontFamily: "'Space Grotesk',sans-serif",
                fontSize: '1rem',
                fontWeight: 700,
                color: '#0f0c29',
                marginBottom: 8
              }}
            >
              {c.title}
            </h3>

            <p
              style={{
                fontSize: '.88rem',
                color: '#6b7280',
                lineHeight: 1.65
              }}
            >
              {c.desc}
            </p>

            <div
              style={{
                position: 'absolute',
                bottom: 0,
                left: 0,
                right: 0,
                height: 3,
                background: `linear-gradient(90deg,${c.color},${c.color}88)`,
                borderRadius: '0 0 20px 20px'
              }}
            />
          </div>
        </Reveal>
      ))}
    </div>
  </div>
</section>

      {/* ══ SERVICES ══ */}
      <section style={{background:'linear-gradient(180deg,#fafafa,#f0f4ff)',padding:sectionPad}}>
        <div className="container">
          <Reveal>
            <div style={{textAlign:'center',marginBottom: isMobile?36:60}}>
              <div className="section-tag" style={{background:'#e0e7ff',color:'#4338ca'}}>Our Services</div>
              <h2 className="section-title" style={{color:'#0f0c29'}}>Everything Electronics, <span style={{color:'#6366f1'}}>Under One Roof</span></h2>
            </div>
          </Reveal>
          <div style={{display:'grid',gridTemplateColumns:svcCols,gap:16}}>
            {services.map((s,i)=>(
              <Reveal key={s.title} delay={i*0.05} direction="up">
                <div className="ch iz" style={{background:'#fff',borderRadius:20,overflow:'hidden',border:'1px solid #e5e7eb',boxShadow:'0 4px 20px rgba(0,0,0,.05)',height:'100%',display:'flex',flexDirection:'column'}}>
                  <div style={{height: isMobile?140:160,overflow:'hidden',position:'relative',flexShrink:0}}>
                    <img src={s.image} alt={s.title} style={{width:'100%',height:'100%',objectFit:'cover'}} loading="lazy"/>
                    <div style={{position:'absolute',inset:0,background:`linear-gradient(to bottom,transparent 40%,${s.color}88)`}}/>
                    <div style={{position:'absolute',bottom:10,left:14,fontSize:'1.5rem'}}>{s.icon}</div>
                  </div>
                  <div style={{padding: isMobile?'14px 16px 18px':'16px 20px 22px',flex:1}}>
                    <h3 style={{fontWeight:700,fontSize:'.92rem',color:'#0f0c29',marginBottom:6}}>{s.title}</h3>
                    <p style={{fontSize:'.8rem',color:'#9ca3af',lineHeight:1.6,marginBottom:12}}>{s.desc}</p>
                    {/* <span style={{fontSize:'.78rem',fontWeight:700,color:s.color}}>Learn more →</span> */}
                  </div>
                  <div style={{height:3,background:`linear-gradient(90deg,${s.color},${s.color}55)`}}/>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ══ INDUSTRIES ══ */}
      <section style={{background:'linear-gradient(135deg,#0f0c29,#1a1045 50%,#0a2463)',padding:sectionPad}}>
        <div className="container">
          <Reveal>
            <div style={{textAlign:'center',marginBottom: isMobile?36:60}}>
              <div className="section-tag" style={{background:'rgba(0,194,224,.15)',color:'#67e8f9',border:'1px solid rgba(0,194,224,.2)'}}>Industries We Serve</div>
              <h2 className="section-title" style={{color:'#fff'}}>Built for <span style={{color:'#67e8f9'}}>Every Sector</span></h2>
            </div>
          </Reveal>
          <div style={{display:'grid',gridTemplateColumns:indCols,gap:16}}>
            {industries.map((ind,i)=>(
              <Reveal key={ind.name} delay={i*0.07} direction="up">
                <a href="/industries" className="ch iz" style={{display:'flex',flexDirection:'column',borderRadius:20,overflow:'hidden',border:'1px solid rgba(255,255,255,.1)',textDecoration:'none',background:'rgba(255,255,255,.04)',height:'100%'}}>
                  <div style={{height: isMobile?160:190,overflow:'hidden',position:'relative',flexShrink:0}}>
                    <img src={ind.image} alt={ind.name} style={{width:'100%',height:'100%',objectFit:'cover'}} loading="lazy"/>
                    <div style={{position:'absolute',inset:0,background:'linear-gradient(to bottom,transparent 30%,rgba(10,14,39,.75))'}}/>
                    <div style={{position:'absolute',top:14,left:14,width:38,height:38,borderRadius:10,background:ind.color,display:'flex',alignItems:'center',justifyContent:'center',fontSize:'1.1rem'}}>{ind.icon}</div>
                  </div>
                  <div style={{padding: isMobile?'16px':' 18px 22px 24px',flex:1}}>
                    <h3 style={{fontFamily:"'Space Grotesk',sans-serif",fontSize:'1rem',fontWeight:700,color:'#fff',marginBottom:6}}>{ind.name}</h3>
                    <p style={{fontSize:'.83rem',color:'rgba(255,255,255,.5)',lineHeight:1.6,marginBottom:12}}>{ind.desc}</p>
                    <div style={{fontSize:'.78rem',fontWeight:700,color:ind.color}}>Explore →</div>
                  </div>
                </a>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ══ CASE STUDIES — stack on mobile, alternating on desktop ══ */}
     <section
  style={{
    background: '#fff',
    padding: sectionPad,
    position: 'relative',
    overflow: 'hidden'
  }}
>
  {/* Triangle Decorations */}
  <div
    style={{
      position: 'absolute',
      top: '-80px',
      left: '-80px',
      width: 0,
      height: 0,
      borderLeft: '180px solid transparent',
      borderRight: '180px solid transparent',
      borderBottom: '180px solid #fef3c7',
      opacity: 0.45,
      transform: 'rotate(-25deg)'
    }}
  />

  <div
    style={{
      position: 'absolute',
      bottom: '-100px',
      right: '-60px',
      width: 0,
      height: 0,
      borderLeft: '220px solid transparent',
      borderRight: '220px solid transparent',
      borderTop: '220px solid #fde68a',
      opacity: 0.35,
      transform: 'rotate(18deg)'
    }}
  />

  <div
    style={{
      position: 'absolute',
      top: '42%',
      right: '12%',
      width: 0,
      height: 0,
      borderLeft: '80px solid transparent',
      borderRight: '80px solid transparent',
      borderBottom: '80px solid #fcd34d',
      opacity: 0.18,
      transform: 'rotate(12deg)'
    }}
  />

  <div className="container" style={{ position: 'relative', zIndex: 2 }}>
    <Reveal>
      <div style={{ textAlign: 'center', marginBottom: isMobile ? 36 : 64 }}>
        <div
          className="section-tag"
          style={{ background: '#fef3c7', color: '#92400e' }}
        >
          Case Studies
        </div>

        <h2
          className="section-title"
          style={{ color: '#0f0c29' }}
        >
          Real Impact, <span style={{ color: '#f59e0b' }}>Real Results</span>
        </h2>
      </div>
    </Reveal>

    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: isMobile ? 32 : 56
      }}
    >
      {caseStudies.map((cs, i) => {
        const flip = !isMobile && i % 2 !== 0;

        return (
          <div
            key={cs.title}
            style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
              gap: isMobile ? 0 : 48,
              alignItems: 'center',
              borderRadius: 20,
              overflow: isMobile ? 'hidden' : 'visible',
              boxShadow: isMobile
                ? `0 4px 24px ${cs.accent}22`
                : 'none',
              border: isMobile
                ? `1px solid ${cs.accent}22`
                : 'none',
              position: 'relative',
              zIndex: 2
            }}
          >
            {/* Image */}
            <div style={{ order: flip ? 1 : 0 }}>
              <Reveal direction={isMobile ? 'up' : flip ? 'right' : 'left'}>
                <div
                  className="iz"
                  style={{
                    borderRadius: isMobile ? '0' : '20px',
                    overflow: 'hidden',
                    boxShadow: isMobile
                      ? 'none'
                      : `0 20px 60px ${cs.accent}33`
                  }}
                >
                  <img
                    src={cs.image}
                    alt={cs.title}
                    style={{
                      width: '100%',
                      height: isMobile ? 200 : 300,
                      objectFit: 'cover',
                      display: 'block'
                    }}
                    loading="lazy"
                  />
                </div>
              </Reveal>
            </div>

            {/* Text */}
            <div
              style={{
                order: flip ? 0 : 1,
                padding: isMobile ? '20px' : 0
              }}
            >
              <Reveal
                direction={isMobile ? 'up' : flip ? 'left' : 'right'}
                delay={isMobile ? 0 : 0.1}
              >
                <span
                  style={{
                    display: 'inline-block',
                    background: `${cs.accent}18`,
                    color: cs.accent,
                    fontSize: '.7rem',
                    fontWeight: 700,
                    letterSpacing: '.08em',
                    textTransform: 'uppercase',
                    padding: '5px 14px',
                    borderRadius: 50,
                    marginBottom: 16
                  }}
                >
                  {cs.tag}
                </span>

                <div
                  style={{
                    fontFamily: "'Space Grotesk',sans-serif",
                    fontSize: isMobile ? '2.2rem' : '3rem',
                    fontWeight: 800,
                    color: cs.accent,
                    lineHeight: 1,
                    marginBottom: 14
                  }}
                >
                  {cs.stat}
                </div>

                <h3
                  style={{
                    fontFamily: "'Space Grotesk',sans-serif",
                    fontSize: isMobile ? '1.1rem' : '1.35rem',
                    fontWeight: 700,
                    color: '#0f0c29',
                    marginBottom: 12,
                    lineHeight: 1.3
                  }}
                >
                  {cs.title}
                </h3>

                <p
                  style={{
                    fontSize: '.92rem',
                    color: '#6b7280',
                    lineHeight: 1.75,
                    marginBottom: 20
                  }}
                >
                  {cs.desc}
                </p>

                <a
                  href="/case-studies"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 8,
                    fontWeight: 700,
                    fontSize: '.88rem',
                    color: cs.accent,
                    border: `2px solid ${cs.accent}33`,
                    padding: '10px 22px',
                    borderRadius: 50,
                    background: `${cs.accent}08`
                  }}
                >
                  Read Full Case Study →
                </a>
              </Reveal>
            </div>
          </div>
        );
      })}
    </div>
  </div>
</section>

      {/* ══ STATS ══ */}
      <section style={{background:'linear-gradient(135deg,#f0f4ff,#faf5ff)',padding:sectionPad}}>
        <div className="container">
          <Reveal>
            <div style={{textAlign:'center',marginBottom: isMobile?36:60}}>
              <div className="section-tag" style={{background:'#e0e7ff',color:'#4338ca'}}>Our Capabilities</div>
              <h2 className="section-title" style={{color:'#0f0c29'}}>Engineered for <span style={{color:'#6366f1'}}>Scale</span></h2>
            </div>
          </Reveal>
          <div style={{display:'grid',gridTemplateColumns:statsCols,gap:12}}>
            {statsData.map((s,i)=>(
              <Reveal key={s.label} delay={i*0.07} direction="up">
                <div className="ch" style={{background:'#fff',borderRadius:20,padding: isMobile?'24px 16px':'32px 20px',textAlign:'center',border:`1px solid ${s.color}22`,boxShadow:`0 4px 20px ${s.color}11`,position:'relative',overflow:'hidden',height:'100%'}}>
                  <div style={{position:'absolute',top:0,left:0,right:0,height:3,background:`linear-gradient(90deg,${s.color},${s.color}88)`}}/>
                  <div style={{fontFamily:"'Space Grotesk',sans-serif",fontSize: isMobile?'1.7rem':'2rem',fontWeight:800,color:s.color,lineHeight:1,marginBottom:6}}>
                    <Counter end={s.value} suffix={s.suffix}/>
                  </div>
                  <div style={{fontSize:'.72rem',color:'#9ca3af',fontWeight:500,lineHeight:1.4}}>{s.label}</div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ══ PROCESS — stack always on mobile ══ */}
      <section style={{background:'#fff',padding:sectionPad}}>
        <div className="container">
          <Reveal>
            <div style={{textAlign:'center',marginBottom: isMobile?36:64}}>
              <div className="section-tag" style={{background:'#d1fae5',color:'#065f46'}}>How It Works</div>
              <h2 className="section-title" style={{color:'#0f0c29'}}>Your Journey from <span style={{color:'#10b981'}}>Concept to Market</span></h2>
            </div>
          </Reveal>

          {isMobile ? (
            /* Mobile: vertical timeline */
            <div style={{display:'flex',flexDirection:'column',gap:24}}>
              {processSteps.map((p,i)=>(
                <Reveal key={p.step} delay={i*0.06} direction="up">
                  <div style={{display:'flex',gap:16,alignItems:'flex-start'}}>
                    {/* Step number */}
                    <div style={{display:'flex',flexDirection:'column',alignItems:'center',flexShrink:0}}>
                      <div style={{width:44,height:44,borderRadius:'50%',background:`linear-gradient(135deg,${p.color},${p.color}88)`,display:'flex',alignItems:'center',justifyContent:'center',fontFamily:"'Space Grotesk',sans-serif",fontSize:'.82rem',fontWeight:800,color:'#fff',boxShadow:`0 6px 18px ${p.color}44`,flexShrink:0}}>{p.step}</div>
                      {i<4&&<div style={{width:2,height:24,background:`linear-gradient(to bottom,${p.color},transparent)`,marginTop:6}}/>}
                    </div>
                    {/* Card */}
                    <div style={{flex:1,background:p.bg,borderRadius:16,overflow:'hidden',border:`1px solid ${p.color}22`}}>
                      <img src={p.img} alt={p.title} style={{width:'100%',height:140,objectFit:'cover',display:'block'}} loading="lazy"/>
                      <div style={{padding:'14px 16px 18px'}}>
                        <h3 style={{fontFamily:"'Space Grotesk',sans-serif",fontSize:'.95rem',fontWeight:700,color:'#0f0c29',marginBottom:6}}>{p.title}</h3>
                        <p style={{fontSize:'.82rem',color:'#6b7280',lineHeight:1.65}}>{p.desc}</p>
                      </div>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          ) : (
            /* Desktop: alternating zigzag */
            <div style={{display:'flex',flexDirection:'column',gap:44}}>
              {processSteps.map((p,i)=>{
                const isEven=i%2===0;
                return(
                  <div key={p.step} style={{display:'grid',gridTemplateColumns:'1fr 64px 1fr',gap:28,alignItems:'center'}}>
                    {isEven?(
                      <>
                        <Reveal direction="left">
                          <div className="iz ch" style={{borderRadius:20,overflow:'hidden',boxShadow:`0 16px 50px ${p.color}22`,height:220}}>
                            <img src={p.img} alt={p.title} style={{width:'100%',height:'100%',objectFit:'cover'}} loading="lazy"/>
                          </div>
                        </Reveal>
                        <div style={{display:'flex',flexDirection:'column',alignItems:'center'}}>
                          <div style={{width:52,height:52,borderRadius:'50%',background:`linear-gradient(135deg,${p.color},${p.color}88)`,display:'flex',alignItems:'center',justifyContent:'center',fontFamily:"'Space Grotesk',sans-serif",fontSize:'.9rem',fontWeight:800,color:'#fff',boxShadow:`0 8px 24px ${p.color}55`,zIndex:2,flexShrink:0}}>{p.step}</div>
                          {i<4&&<div style={{width:2,flex:1,background:`linear-gradient(to bottom,${p.color},transparent)`,minHeight:36}}/>}
                        </div>
                        <Reveal direction="right" delay={0.1}>
                          <div style={{background:p.bg,borderRadius:20,padding:28,border:`1px solid ${p.color}22`}}>
                            <h3 style={{fontFamily:"'Space Grotesk',sans-serif",fontSize:'1.1rem',fontWeight:700,color:'#0f0c29',marginBottom:10}}>{p.title}</h3>
                            <p style={{fontSize:'.9rem',color:'#6b7280',lineHeight:1.7}}>{p.desc}</p>
                          </div>
                        </Reveal>
                      </>
                    ):(
                      <>
                        <Reveal direction="left">
                          <div style={{background:p.bg,borderRadius:20,padding:28,border:`1px solid ${p.color}22`}}>
                            <h3 style={{fontFamily:"'Space Grotesk',sans-serif",fontSize:'1.1rem',fontWeight:700,color:'#0f0c29',marginBottom:10}}>{p.title}</h3>
                            <p style={{fontSize:'.9rem',color:'#6b7280',lineHeight:1.7}}>{p.desc}</p>
                          </div>
                        </Reveal>
                        <div style={{display:'flex',flexDirection:'column',alignItems:'center'}}>
                          <div style={{width:52,height:52,borderRadius:'50%',background:`linear-gradient(135deg,${p.color},${p.color}88)`,display:'flex',alignItems:'center',justifyContent:'center',fontFamily:"'Space Grotesk',sans-serif",fontSize:'.9rem',fontWeight:800,color:'#fff',boxShadow:`0 8px 24px ${p.color}55`,zIndex:2,flexShrink:0}}>{p.step}</div>
                          {i<4&&<div style={{width:2,flex:1,background:`linear-gradient(to bottom,${p.color},transparent)`,minHeight:36}}/>}
                        </div>
                        <Reveal direction="right" delay={0.1}>
                          <div className="iz ch" style={{borderRadius:20,overflow:'hidden',boxShadow:`0 16px 50px ${p.color}22`,height:220}}>
                            <img src={p.img} alt={p.title} style={{width:'100%',height:'100%',objectFit:'cover'}} loading="lazy"/>
                          </div>
                        </Reveal>
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* ══ CTA ══ */}
      <section style={{background:'linear-gradient(135deg,#0f0c29,#1a1045 50%,#0a2463)',padding: isMobile?'70px 0':'120px 0',position:'relative',overflow:'hidden'}}>
        <div style={{position:'absolute',width:600,height:600,top:-300,right:-100,borderRadius:'50%',background:'radial-gradient(circle,rgba(99,102,241,.15) 0%,transparent 70%)',pointerEvents:'none'}}/>
        <div className="container" style={{position:'relative',zIndex:2}}>
          <Reveal direction="up">
            <div style={{textAlign:'center',maxWidth:680,margin:'0 auto'}}>
              <div className="section-tag" style={{background:'rgba(99,102,241,.2)',color:'#a5b4fc',border:'1px solid rgba(99,102,241,.3)',marginBottom:20}}>🚀 Start Your Journey</div>
              <h2 style={{fontFamily:"'Space Grotesk',sans-serif",fontSize:'clamp(1.8rem,5vw,3.2rem)',fontWeight:800,color:'#fff',lineHeight:1.15,marginBottom:16}}>
                Ready to Build Your Next<br/><span style={{color:'#67e8f9'}}>Electronic Product?</span>
              </h2>
              <p style={{fontSize: isMobile?'.95rem':'1.05rem',color:'rgba(255,255,255,.55)',lineHeight:1.75,marginBottom:36}}>Partner with Zxeon FS for innovative design and reliable manufacturing.</p>
              <div style={{display:'flex',justifyContent:'center',gap:12,flexWrap:'wrap'}}>
                <a href="/contact" className="btn-p">Get a Free Quote →</a>
                <a href="/about" className="btn-s">Learn About Us</a>
              </div>
            </div>
          </Reveal>
          {/* Trust tiles */}
          <div style={{display:'grid',gridTemplateColumns:ctaTrust,gap:12,marginTop: isMobile?40:64}}>
            {[{icon:'🏆',label:'ISO 9001 Certified',color:'#fcd34d'},{icon:'🌿',label:'RoHS Compliant',color:'#6ee7b7'},{icon:'🔬',label:'Class 100K Clean Room',color:'#a5b4fc'},{icon:'🛡️',label:'100% Traceability',color:'#f9a8d4'}].map(t=>(
              <div key={t.label} style={{background:'rgba(255,255,255,.05)',border:'1px solid rgba(255,255,255,.08)',borderRadius:16,padding: isMobile?'16px 12px':'20px 16px',textAlign:'center',backdropFilter:'blur(10px)'}}>
                <div style={{fontSize:'1.6rem',marginBottom:8}}>{t.icon}</div>
                <div style={{fontSize:'.75rem',fontWeight:700,color:t.color,lineHeight:1.4}}>{t.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
}